import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Cctv,
  Globe,
  Laptop,
  Network,
  Server,
  Settings,
  Shield,
  Sparkles,
  Tv,
  Wifi,
} from 'lucide-react';

const services = [
  {
    icon: Network,
    title: 'Networking',
    description:
      'Design, setup, and maintenance for robust wired and wireless infrastructure that keeps teams connected.',
    features: [
      'Network design and architecture',
      'LAN and WAN setup',
      'WiFi optimisation',
      'Security and monitoring',
    ],
    color: 'from-sky-500 to-cyan-400',
  },
  {
    icon: Laptop,
    title: 'IT Support',
    description:
      'Remote and on-site support focused on practical fixes, clear communication, and fewer interruptions.',
    features: [
      'Remote and on-site support',
      'Hardware and software troubleshooting',
      'Help desk assistance',
      'Preventive maintenance',
    ],
    color: 'from-fuchsia-500 to-rose-400',
  },
  {
    icon: Wifi,
    title: 'Starlink Installation',
    description:
      'Professional Starlink installs with clean mounting, reliable setup, and thoughtful cable routing.',
    features: [
      'Site assessment and dish placement',
      'Professional mounting',
      'Cable routing and weatherproofing',
      'Network setup and optimisation',
    ],
    color: 'from-blue-600 to-sky-400',
  },
  {
    icon: Globe,
    title: 'Web Development',
    description:
      'Modern websites built to look credible, work smoothly, and help your business show up better online.',
    features: [
      'Custom website design',
      'Responsive mobile-friendly builds',
      'E-commerce solutions',
      'SEO-friendly structure',
    ],
    color: 'from-emerald-500 to-lime-400',
  },
  {
    icon: Cctv,
    title: 'Security Systems',
    description:
      'Dependable surveillance and access solutions designed to protect homes, businesses, and critical spaces.',
    features: [
      'CCTV camera installation',
      'Video surveillance systems',
      'Access control systems',
      'Remote monitoring solutions',
    ],
    color: 'from-orange-500 to-red-400',
  },
  {
    icon: Tv,
    title: 'TV Wall Mounting',
    description:
      'Clean, professional TV mounting with tidy finishing, cable management, and thoughtful placement.',
    features: [
      'Mounting for major TV sizes and brands',
      'Cable hiding and management',
      'Drywall and masonry mounting',
      'Soundbar and accessory setup',
    ],
    color: 'from-slate-700 to-slate-500',
  },
  {
    icon: Settings,
    title: 'System Administration',
    description:
      'Hands-on admin support for systems, backups, updates, accounts, and day-to-day operational stability.',
    features: [
      'Server maintenance',
      'User account administration',
      'Backup and disaster recovery',
      'Updates and patching',
    ],
    color: 'from-indigo-500 to-blue-400',
  },
  {
    icon: Server,
    title: 'Cloud Services',
    description:
      'Scalable cloud solutions that help modernise infrastructure while staying flexible and manageable.',
    features: [
      'Cloud migration',
      'Cloud backup solutions',
      'Virtual server hosting',
      'Cloud security support',
    ],
    color: 'from-teal-500 to-emerald-400',
  },
];

const serviceHighlights = [
  {
    icon: Shield,
    title: 'Reliable Work',
    description: 'Services designed around stability, clear delivery, and less rework later.',
  },
  {
    icon: Sparkles,
    title: 'Broad Capability',
    description: 'One team covering support, installs, websites, infrastructure, and systems.',
  },
  {
    icon: CheckCircle2,
    title: 'Straightforward Process',
    description: 'Clear advice, practical recommendations, and service you can actually act on.',
  },
];

const reasons = [
  {
    title: 'Regional and Australia-wide support',
    description:
      'Support across multiple cities and flexible service regions, with solutions tailored to the job at hand.',
  },
  {
    title: 'Fast and practical response',
    description:
      'The focus is on getting to the right fix quickly, whether that means support, setup, or a scoped project.',
  },
  {
    title: 'Built around outcomes',
    description:
      'Every service is delivered with the end result in mind: reliability, clarity, and smoother day-to-day use.',
  },
];

const Services = () => {
  return (
    <div className="overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white">
      <section
        className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10"
        data-testid="services-hero"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.8),transparent_45%)] dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_35%)]" />
        </div>

        <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-[1.08fr_0.92fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
              Service support across multiple cities and Australian regions
            </div>
            <h1 className="max-w-3xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-7xl dark:text-white">
              Services built to keep
              <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                systems steady, projects moving, and support simple.
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
              Connectify covers the practical technology work that homes and businesses depend on,
              from networking and support to Starlink installs, web development, security systems,
              and ongoing technical administration.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-500 dark:text-slate-400">
              Services are available across Australia, including Tasmania, Darwin, Canberra, and
              surrounding service regions.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/booking"
                data-testid="services-booking-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-[#1E90FF] px-8 py-4 font-semibold text-white shadow-[0_18px_45px_-18px_rgba(30,144,255,0.45)] transition duration-300 hover:-translate-y-1 hover:bg-[#1570d6] hover:shadow-[0_20px_50px_-20px_rgba(30,144,255,0.55)]"
              >
                <span>Book a consultation</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white/80 px-8 py-4 font-semibold text-slate-800 backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-slate-900 hover:text-slate-950 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:border-cyan-300 dark:hover:text-cyan-200"
              >
                Ask about your project
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-400/20 via-cyan-300/10 to-emerald-300/20 blur-2xl dark:from-sky-400/15 dark:to-emerald-400/10" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-slate-950 p-8 text-white shadow-[0_30px_80px_-24px_rgba(2,12,27,0.7)] dark:border-white/10">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <p className="text-sm uppercase tracking-[0.25em] text-sky-200/80">Connectify</p>
                  <h2 className="mt-2 text-2xl font-bold">What you can expect</h2>
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-sky-100">
                  Service-first
                </div>
              </div>

              <div className="mt-6 grid gap-4">
                {serviceHighlights.map((item) => (
                  <div key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                    <div className="flex items-start gap-4">
                      <div className="rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-300 p-3 text-slate-950">
                        <item.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">{item.title}</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-300">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-5 text-sm text-emerald-100">
                Whether the job is a one-off install or part of a broader support setup, the goal
                stays the same: dependable work, clear communication, and a better end result.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="services-grid">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
                Service Catalogue
              </p>
              <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
                Support, installs, and technical work designed around what actually needs doing.
              </h2>
            </div>
            <p className="max-w-xl text-base leading-7 text-slate-600 dark:text-slate-300">
              Each service area below is built around practical delivery, not vague promises. Book
              directly from the service that fits what you need.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {services.map((service) => (
              <div
                key={service.title}
                data-testid={`service-detail-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] p-8 transition duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_24px_65px_-28px_rgba(14,165,233,0.55)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.58))] dark:hover:border-cyan-300/60 dark:hover:shadow-[0_24px_65px_-28px_rgba(34,211,238,0.35)]"
              >
                <div className={`flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${service.color} shadow-lg`}>
                  <service.icon className="h-8 w-8 text-white" />
                </div>

                <h3 className="mt-6 text-2xl font-bold text-slate-950 transition-colors group-hover:text-sky-700 dark:text-white dark:group-hover:text-cyan-300">
                  {service.title}
                </h3>
                <p className="mt-4 text-base leading-7 text-slate-600 dark:text-slate-300">
                  {service.description}
                </p>

                <ul className="mt-6 space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600 dark:bg-cyan-300" />
                      <span className="text-sm leading-6 text-slate-700 dark:text-slate-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/booking?service=${encodeURIComponent(service.title)}`}
                  data-testid={`request-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 transition hover:gap-3 dark:text-cyan-300"
                >
                  Request this service
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200/70 bg-slate-950 py-20 text-white dark:border-white/10" data-testid="why-choose-us">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Why Choose Connectify
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              A service approach built around reliability, communication, and usable outcomes.
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {reasons.map((reason, index) => (
              <div key={reason.title} data-testid={`benefit-${index}`} className="rounded-[1.75rem] border border-white/10 bg-white/5 p-8 backdrop-blur">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-300 text-lg font-black text-slate-950">
                  0{index + 1}
                </div>
                <h3 className="mt-6 text-xl font-semibold">{reason.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 pt-6" data-testid="services-cta">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] bg-[linear-gradient(135deg,#0f172a_0%,#0f766e_100%)] p-10 text-white shadow-[0_35px_90px_-35px_rgba(15,23,42,0.8)] sm:p-14">
            <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="max-w-2xl">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200">
                  Ready To Get Started
                </p>
                <h2 className="mt-4 text-3xl font-black sm:text-4xl">
                  Choose the service you need and get the right work underway.
                </h2>
                <p className="mt-4 text-base leading-7 text-slate-200">
                  If you already know what you need, book now. If the job needs a quick conversation
                  first, reach out and we’ll help scope the best next step.
                </p>
              </div>

              <Link
                to="/booking"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 font-semibold text-slate-950 transition duration-300 hover:-translate-y-1 hover:bg-slate-100"
              >
                <span>Book now</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
